//* This file contains the API utility object for managing CRUD operations for units.
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Type for pagination data
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import { type Unit, type UnitResponse, type UnitDetails } from "./types/types";

/**
 * UnitAPI is an API utility object to manage CRUD operations (create, read, update, delete)
 * for units through HTTP requests. It interacts with a backend API and handles operations
 * such as fetching a list of units, retrieving a unit by ID, creating, updating, and deleting units.
 */
export const unitAPI = {
  /**
   * Fetches a paginated list of units from the API.
   *
   * @param {string} search - Optional search term to filter units by name (default is an empty string).
   * @param {number} page - Page number for pagination (default is 1).
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {number} perPage - Number of units per page (default is 10).
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<UnitResponse>} - Returns a paginated response containing units and pagination info.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<UnitResponse> {
    const searchQuery = search ? `&search=${search}` : ""; // Add search query if provided
    const url = `${API_URI}/admin/private/units?page=${page}&perPage=${perPage}${searchQuery}`; // API endpoint for fetching units

    return api
      .request({
        url: url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          units: data.units || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching units:", error);
        throw new Error("Failed to fetch units");
      });
  },

  /**
   * Fetches a specific unit by its ID from the API.
   *
   * @param {string} id - UUID of the unit to fetch.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Unit>} - Returns the unit data if found.
   *
   * @throws {Error} - Throws an error if the unit is not found or if the response is invalid.
   */
  getById: async function (
    id: string,
    token: string,
    options: {
      searchTermStudents?: string;
      studentFilter?: "all" | "active" | "inactive";
      pageStudents?: number;
      perPageStudents?: number;
      searchTermTeams?: string;
      pageTeams?: number;
      perPageTeams?: number;
    } = {},
    cancel = false
  ): Promise<UnitDetails> {
    const {
      searchTermStudents,
      studentFilter,
      pageStudents,
      perPageStudents,
      searchTermTeams,
      pageTeams,
      perPageTeams,
    } = options;

    const params = new URLSearchParams();

    if (searchTermStudents)
      params.append("searchTermStudents", searchTermStudents);
    if (studentFilter) params.append("studentFilter", studentFilter);
    if (pageStudents) params.append("pageStudents", pageStudents.toString());
    if (perPageStudents)
      params.append("perPageStudents", perPageStudents.toString());
    if (searchTermTeams) params.append("searchTermTeams", searchTermTeams);
    if (pageTeams) params.append("pageTeams", pageTeams.toString());
    if (perPageTeams) params.append("perPageTeams", perPageTeams.toString());

    return api
      .request({
        url: `${API_URI}/admin/private/units/${id}?${params.toString()}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          const data = response.data.data;

          // Transformar as propriedades de paginação para camelCase
          if (data.teams && data.teams.pagination) {
            const { current_page, per_page, total_pages } =
              data.teams.pagination;
            data.teams.pagination = {
              currentPage: current_page,
              perPage: per_page,
              totalPages: total_pages,
            };
          }

          if (data.students && data.students.pagination) {
            const { current_page, per_page, total_pages } =
              data.students.pagination;
            data.students.pagination = {
              currentPage: current_page,
              perPage: per_page,
              totalPages: total_pages,
            };
          }

          return data; // Retorna os dados com as propriedades ajustadas
        } else {
          throw new Error("Unit not found or invalid response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching unit:", error); // Log for debugging
        throw new Error("Failed to fetch unit");
      });
  },

  /**
   * Creates a new unit by sending a POST request to the API.
   *
   * @param {Object} data - Data for the new unit, including name and description.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Unit>} - Returns the created unit data.
   *
   * @throws {Error} - Throws an error if the unit creation fails.
   */
  create: async function (
    data: { name: string; description: string },
    token: string,
    cancel = false
  ): Promise<Unit> {
    return api
      .request({
        url: `${API_URI}/admin/private/units`, // API endpoint for creating a unit
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data, // Unit data to be sent in the request body
      })
      .then((response) => response.data.data) // Return the response data (created unit)
      .catch((error) => {
        console.error("Error creating unit:", error); // Log for debugging
        throw new Error("Failed to create unit");
      });
  },

  /**
   * Updates an existing unit by sending a PUT request to the API.
   *
   * @param {string} id - UUID of the unit to update.
   * @param {Object} data - Updated unit data including name and description.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<Unit>} - Returns the updated unit data.
   *
   * @throws {Error} - Throws an error if the unit update fails.
   */
  update: async function (
    id: string,
    data: { name: string; description: string },
    token: string,
    cancel = false
  ): Promise<Unit> {
    return api
      .request({
        url: `${API_URI}/admin/private/units/${id}`, // API endpoint for updating a unit
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data, // Updated unit data to be sent in the request body
      })
      .then((response) => response.data.data) // Return the updated unit data
      .catch((error) => {
        console.error("Error updating unit:", error); // Log for debugging
        throw new Error("Failed to update unit");
      });
  },

  /**
   * Deletes an existing unit by sending a DELETE request to the API.
   *
   * @param {string} id - UUID of the unit to delete.
   * @param {string} token - Authentication token to be sent in the request headers.
   * @param {boolean} cancel - Optional flag for cancellation token, if supported (default is false).
   *
   * @returns {Promise<any>} - Returns the result of the deletion.
   *
   * @throws {Error} - Throws an error if the deletion fails.
   */
  delete: async function (id: string, token: string, cancel = false) {
    return api
      .request({
        url: `${API_URI}/admin/private/units/${id}`, // API endpoint for deleting a unit
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data) // Return the response data (deletion result)
      .catch((error) => {
        console.error("Error deleting unit:", error); // Log for debugging
        throw new Error("Failed to delete unit");
      });
  },
};
